import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../stores/auth';

export default function Login() {
  const [documento, setDocumento] = useState('');
  const [pass,      setPass]      = useState('');
  const [showPass,  setShowPass]  = useState(false);
  const [loading,   setLoading]   = useState(false);
  const [resetting, setResetting] = useState(false);
  const [err,       setErr]       = useState<string | null>(null);
  const nav        = useNavigate();
  const setSession = useAuth((s) => s.setSession);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!documento.trim()) return setErr('Ingresá tu documento');
    if (!pass)             return setErr('Ingresá tu contraseña');
    setLoading(true);
    setErr(null);
    try {
      const data = await api<{ access: string; refresh: string; ciudadano: any }>(
        '/auth?action=login',
        { method: 'POST', body: JSON.stringify({ documento, password: pass }), auth: false }
      );
      setSession(data.access, data.refresh, data.ciudadano);
      nav('/', { replace: true });
    } catch (e: any) {
      setErr(e.message || 'Error al ingresar');
    } finally {
      setLoading(false);
    }
  }

  async function olvidéContraseña() {
    const doc = documento.trim();
    if (!doc) return setErr('Ingresá primero tu número de documento y después tocá "¿Olvidaste tu contraseña?"');
    if (!confirm(`Si el documento ${doc} tiene un email registrado, te mandaremos un enlace para restablecer la contraseña.\n\nEl enlace vale 1 hora. ¿Enviamos?`)) return;
    setResetting(true);
    setErr(null);
    try {
      const res = await api<{ message?: string }>('/auth?action=reset_password', {
        method: 'POST',
        body: JSON.stringify({ documento: doc }),
        auth: false,
      });
      alert(
        (res as any)?.message ||
        'Si el documento existe y tiene un email, te enviamos el enlace. Revisá también tu carpeta de spam.'
      );
    } catch (e: any) {
      setErr(e.message || 'No se pudo procesar la solicitud.');
    } finally {
      setResetting(false);
    }
  }

  return (
    <main className="min-h-screen flex flex-col items-center justify-center px-6" style={{ backgroundColor: '#f2f2f2' }}>
      <div className="w-full max-w-sm">

        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <img src={`${import.meta.env.BASE_URL}icon.png`} alt="CiudadApp" className="w-20 h-20 rounded-2xl mb-4 shadow-md" />
          <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">CiudadApp</h1>
          <p className="text-gray-500 text-sm mt-1">Tu municipio en tu bolsillo</p>
        </div>

        {/* Card */}
        <div className="rounded-2xl p-6 shadow-sm" style={{ backgroundColor: '#ffffff', border: '1px solid #6e6b6b' }}>
          <p className="font-bold text-gray-900 text-sm mb-5">Iniciá sesión</p>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={{ color: '#64748b' }}>Número de documento</label>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="username"
                value={documento}
                onChange={(e) => setDocumento(e.target.value.replace(/\D/g, ''))}
                placeholder="Ej: 30123456"
                className="w-full rounded-xl px-4 py-3 text-sm outline-none"
                style={{ backgroundColor: '#ffffff', border: '1px solid #6e6b6b', color: '#303030' }}
              />
            </div>

            <div>
              <label className="block text-xs font-semibold mb-1.5" style={{ color: '#64748b' }}>Contraseña</label>
              <div className="relative">
                <input
                  type={showPass ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={pass}
                  onChange={(e) => setPass(e.target.value)}
                  placeholder="••••••••"
                  className="w-full rounded-xl px-4 py-3 pr-12 text-sm outline-none"
                  style={{ backgroundColor: '#ffffff', border: '1px solid #6e6b6b', color: '#303030' }}
                />
                <button
                  type="button"
                  onClick={() => setShowPass((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-xl leading-none"
                  tabIndex={-1}
                >
                  {showPass ? '🐵' : '🙈'}
                </button>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={olvidéContraseña}
                disabled={resetting}
                className="text-xs text-brand-600 font-medium disabled:opacity-50"
              >
                {resetting ? 'Enviando…' : '¿Olvidaste tu contraseña?'}
              </button>
            </div>

            {err && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl p-3">{err}</div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white rounded-xl py-3.5 font-bold text-base"
            >
              {loading ? 'Ingresando…' : 'Ingresar'}
            </button>
          </form>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-gray-400 text-xs">¿No tenés cuenta?</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          <button
            type="button"
            onClick={() => nav('/registro')}
            className="w-full border-2 border-brand-600 text-brand-600 hover:bg-brand-50 rounded-xl py-3 font-bold text-sm"
          >
            Crear cuenta →
          </button>
        </div>

        {/* Acceso personal municipal */}
        <button
          onClick={() => nav('/login/staff')}
          className="w-full mt-4 text-center text-gray-500 text-sm py-3 rounded-xl hover:bg-gray-100"
        >
          🏢 Acceso personal municipal
        </button>

      </div>
    </main>
  );
}
